// components/Titles/SecurityForm.jsx - Formulaire unifié pour création/modification
import React, { useState, useEffect } from 'react';
import { createSecurity, updateSecurity, getSecurityById } from '../../service/api/SecurityService';
import { getAccounts } from '../../service/api/Order';

import { toast } from 'sonner';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import SearchableSelect from '../../SearchableSelect';

/**
 * Formulaire unifié pour la création et modification de titres
 * Supporte à la fois le modal ID 'moredetailSecurities' et 'securityFormModal'
 */
const SecurityForm = ({ 
  securityId = null, 
  onSuccess, 
  onClose,
  modalId = 'moredetailSecurities' // ID du modal par défaut
}) => {
  const selectedDomain = useSelectedDomain();
  
  // État du formulaire
  const [formData, setFormData] = useState({
    issuer_account_id: '',
    symbol: '',
    isin: '',
    name: '',
    target_market: '',
    type: '',
    repayable: '',
    total_units_issue: '',
    maturity: '',
    coupon_frequency: '',
    coupon_rate: '',
    issue_price: '',
    last_price: '',
    issue_date: '',
    date_jouissance: ''
  });
  
  const [accounts, setAccounts] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Charger les comptes
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!selectedDomain?.id) return;

      try {
        const accountsData = await getAccounts(selectedDomain.id);
        
        if (!accountsData.items || accountsData.items.length === 0) {
          setAccounts([]);
          setNoResults(true);
        } else {
          setAccounts(accountsData.items);
          setNoResults(false);
        }
      } catch (error) {
        // console.error("Erreur lors de la récupération des comptes:", error);
        if (error.response?.status === 404) {
          setAccounts([]);
          setNoResults(true);
        } else {
          toast.error("Erreur lors de la récupération des comptes.");
        }
      }
    };

    fetchAccounts();
  }, [selectedDomain]);

  // Charger les données du titre si en mode édition
  useEffect(() => {
    const loadSecurityData = async () => {
      if (securityId) {
        try {
          setIsLoading(true);
          const result = await getSecurityById(securityId);
          
          if (result.success && result.data) {
            // Mapper les données de l'API vers le format du formulaire
            const mappedData = {
              issuer_account_id: result.data.issuerAccountId?.toString() || '',
              symbol: result.data.symbol || '',
              isin: result.data.isin || '',
              name: result.data.name || '',
              target_market: result.data.targetMarket || '',
              type: result.data.type || '',
              repayable: result.data.repayable?.toString() || '',
              total_units_issue: result.data.totalUnitsIssue?.toString() || '',
              maturity: result.data.maturity ? new Date(result.data.maturity).toISOString().split('T')[0] : '',
              coupon_frequency: result.data.couponFrequency || '',
              coupon_rate: result.data.couponRate?.toString() || '',
              issue_price: result.data.issuePrice?.toString() || '',
              last_price: result.data.lastPrice?.toString() || '',
              issue_date: result.data.issueDate ? new Date(result.data.issueDate).toISOString().split('T')[0] : '',
              date_jouissance: result.data.dateJouissance ? new Date(result.data.dateJouissance).toISOString().split('T')[0] : ''
            };
            
            setFormData(mappedData);
            setIsEditMode(true);
          }
        } catch (error) {
          // console.error(' Erreur lors du chargement du titre:', error);
          toast.error(error.message || 'Erreur lors du chargement du titre');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadSecurityData();
  }, [securityId]);

  /**
   * Gérer le changement des champs
   */
  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    const newValue = inputType === "checkbox" ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Logique spéciale pour le type
    if (name === "type") {
      handleTypeChange(newValue);
    }
  };

  /**
   * Gérer le changement de type de titre
   */
  const handleTypeChange = (newType) => {
    const cleanedData = { ...formData, type: newType };
    
    // Pour les actions, réinitialiser les champs non applicables
    if (newType === "Shares" || newType === "ETF" || newType === "Actions") {
      cleanedData.coupon_frequency = "";
      cleanedData.coupon_rate = "";
      cleanedData.repayable = "";
      cleanedData.maturity = "";
      
      // Pour les actions, date d'émission par défaut = aujourd'hui
      if (!cleanedData.issue_date) {
        cleanedData.issue_date = new Date().toISOString().split('T')[0];
      }
    }
    
    // Pour les obligations, s'assurer que last_price = issue_price si vide
    if ((newType === "Bonds" || newType === "Notes") && !cleanedData.last_price && cleanedData.issue_price) {
      cleanedData.last_price = cleanedData.issue_price;
    }
    
    setFormData(cleanedData);
    
    // Nettoyer les erreurs des champs qui deviennent non applicables
    if (newType === "Shares" || newType === "ETF") {
      const { coupon_frequency, coupon_rate, repayable, maturity, ...remainingErrors } = errors;
      setErrors(remainingErrors);
    }
  };

  /**
   * Gérer le blur (champ touché)
   */
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field);
  };

  /**
   * Valider un champ spécifique
   */
  const validateField = (fieldName) => {
    const value = formData[fieldName];
    let error = null;

    switch (fieldName) {
      case 'issuer_account_id':
        if (!value) error = "Le compte de l'émetteur est requis";
        break;
      case 'symbol':
        if (!value?.trim()) error = "Le symbole est requis";
        else if (value.length > 10) error = "Le symbole ne peut pas dépasser 10 caractères";
        break;
      case 'isin':
        if (!value?.trim()) error = "L'ISIN est requis";
        else if (value.length !== 12) error = "L'ISIN doit contenir exactement 12 caractères";
        else if (!/^[A-Z]{2}[A-Z0-9]{10}$/.test(value.toUpperCase())) {
          error = "Format ISIN invalide (2 lettres + 10 caractères)";
        }
        break;
      case 'name':
        if (!value?.trim()) error = "Le nom du titre est requis";
        break;
      case 'type':
        if (!value) error = "Le type de titre est requis";
        break;
      case 'total_units_issue':
        if (!value || parseFloat(value) <= 0) error = "Doit être un nombre positif";
        break;
      case 'issue_price':
        if (!value || parseFloat(value) <= 0) error = "Doit être supérieur à 0";
        break;
      case 'coupon_rate':
        if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
          error = "Doit être un pourcentage entre 0 et 100";
        }
        break;
      case 'maturity':
        if ((formData.type === "Bonds" || formData.type === "Notes") && !value) {
          error = "La date d'échéance est requise";
        }
        break;
      case 'issue_date':
        if ((formData.type === "Bonds" || formData.type === "Notes") && !value) {
          error = "La date d'émission est requise";
        }
        break;
      case 'date_jouissance':
        if (value && formData.issue_date && new Date(value) > new Date(formData.issue_date)) {
          error = "La date de jouissance ne peut pas être après la date d'émission";
        }
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    } else if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  /**
   * Valider tout le formulaire
   */
  const validateForm = () => {
    const newErrors = {};

    // Validation des champs obligatoires communs
    if (!formData.issuer_account_id) {
      newErrors.issuer_account_id = "Le compte de l'émetteur est requis";
    }

    if (!formData.symbol?.trim()) {
      newErrors.symbol = "Le symbole est requis";
    } else if (formData.symbol.length > 10) {
      newErrors.symbol = "Le symbole ne peut pas dépasser 10 caractères";
    }

    if (!formData.isin?.trim()) {
      newErrors.isin = "L'ISIN est requis";
    } else if (formData.isin.length !== 12) {
      newErrors.isin = "L'ISIN doit contenir exactement 12 caractères";
    } else if (!/^[A-Z]{2}[A-Z0-9]{10}$/.test(formData.isin.toUpperCase())) {
      newErrors.isin = "Format ISIN invalide (2 lettres + 10 caractères)";
    }

    if (!formData.name?.trim()) {
      newErrors.name = "Le nom du titre est requis";
    }

    if (!formData.type) {
      newErrors.type = "Le type de titre est requis";
    }

    if (!formData.total_units_issue || parseFloat(formData.total_units_issue) <= 0) {
      newErrors.total_units_issue = "Doit être un nombre positif";
    }

    if (!formData.issue_price || parseFloat(formData.issue_price) <= 0) {
      newErrors.issue_price = "Doit être supérieur à 0";
    }

    // Validation conditionnelle selon le type
    if (formData.type === "Bonds" || formData.type === "Notes") {
      if (!formData.maturity) {
        newErrors.maturity = "La date d'échéance est requise";
      }

      if (!formData.issue_date) {
        newErrors.issue_date = "La date d'émission est requise";
      }

      if (formData.coupon_rate && (parseFloat(formData.coupon_rate) < 0 || parseFloat(formData.coupon_rate) > 100)) {
        newErrors.coupon_rate = "Doit être un pourcentage entre 0 et 100";
      }
    }

    // Validation des dates
    if (formData.maturity && formData.issue_date) {
      const maturityDate = new Date(formData.maturity);
      const issueDate = new Date(formData.issue_date);
      
      if (maturityDate <= issueDate) {
        newErrors.maturity = "La date d'échéance doit être postérieure à la date d'émission";
      }
    }

    if (formData.date_jouissance && formData.issue_date) {
      const jouissanceDate = new Date(formData.date_jouissance);
      const issueDate = new Date(formData.issue_date);
      
      if (jouissanceDate > issueDate) {
        newErrors.date_jouissance = "La date de jouissance ne peut pas être après la date d'émission";
      }
    }

    // Validation pour les actions - champs non autorisés
    if (formData.type === "Shares" || formData.type === "ETF") {
      if (formData.coupon_rate) {
        newErrors.coupon_rate = "Le taux de coupon n'est pas applicable pour ce type de titre";
      }

      if (formData.coupon_frequency) {
        newErrors.coupon_frequency = "La fréquence du coupon n'est pas applicable pour ce type de titre";
      }

      if (formData.repayable) {
        newErrors.repayable = "Le champ remboursable n'est pas applicable pour ce type de titre";
      }

      if (formData.maturity) {
        newErrors.maturity = "La date d'échéance n'est pas applicable pour ce type de titre";
      }
    }

    return newErrors;
  };

  /**
   * Soumettre le formulaire
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Marquer tous les champs comme touchés
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Valider le formulaire
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setIsSubmitting(false);
      toast.error("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }

    // Préparer les données pour l'API
    const dataToSend = new FormData();
    
    // Champs communs
    dataToSend.append('IssuerAccountId', parseInt(formData.issuer_account_id, 10));
    dataToSend.append('Symbol', formData.symbol.trim().toUpperCase());
    dataToSend.append('Isin', formData.isin.trim().toUpperCase());
    dataToSend.append('Name', formData.name.trim());
    dataToSend.append('TargetMarket', formData.target_market || 'Private');
    dataToSend.append('Type', formData.type);
    
    // Champs conditionnels
    if (formData.repayable && (formData.type === "Bonds" || formData.type === "Notes")) {
      dataToSend.append('Repayable', formData.repayable === 'true');
    }
    
    if (formData.total_units_issue) {
      dataToSend.append('TotalUnitsIssue', parseInt(formData.total_units_issue, 10));
    }
    
    if (formData.maturity && (formData.type === "Bonds" || formData.type === "Notes")) {
      dataToSend.append('Maturity', new Date(formData.maturity).toISOString());
    }
    
    if (formData.coupon_frequency && (formData.type === "Bonds" || formData.type === "Notes")) {
      dataToSend.append('CouponFrequency', formData.coupon_frequency);
    }
    
    if (formData.coupon_rate && (formData.type === "Bonds" || formData.type === "Notes")) {
      dataToSend.append('CouponRate', parseFloat(formData.coupon_rate));
    }
    
    dataToSend.append('IssuePrice', parseFloat(formData.issue_price));
    
    // Dernier prix - utiliser issue_price comme valeur par défaut
    const lastPrice = formData.last_price || formData.issue_price;
    dataToSend.append('LastPrice', parseFloat(lastPrice));
    
    // Dates
    if (formData.issue_date) {
      dataToSend.append('IssueDate', new Date(formData.issue_date).toISOString());
    }
    
    if (formData.date_jouissance) {
      dataToSend.append('DateJouissance', new Date(formData.date_jouissance).toISOString());
    }
    
    // Ajouter l'ID en mode édition
    if (isEditMode && securityId) {
      dataToSend.append('Id', securityId);
    }


    try {
      let result;
      
      if (isEditMode) {
        result = await updateSecurity(dataToSend);
      } else {
        result = await createSecurity(dataToSend);
      }

      if (result.success) {
        toast.success(result.message || (isEditMode ? "Titre mis à jour avec succès" : "Titre créé avec succès"));
        
        // Réinitialiser le formulaire
        setFormData({
          issuer_account_id: '',
          symbol: '',
          isin: '',
          name: '',
          target_market: '',
          type: '',
          repayable: '',
          total_units_issue: '',
          maturity: '',
          coupon_frequency: '',
          coupon_rate: '',
          issue_price: '',
          last_price: '',
          issue_date: '',
          date_jouissance: ''
        });
        setErrors({});
        setTouched({});
        
        // Notifier le succès
        if (onSuccess) {
          onSuccess(result.data, !isEditMode);
        }
        
        // Fermer le modal
        handleClose();
      } else {
        // Gérer les erreurs de l'API
        if (result.errors && Object.keys(result.errors).length > 0) {
          const mappedErrors = {};
          Object.entries(result.errors).forEach(([field, messages]) => {
            // Mapper les noms de champs de l'API au format du formulaire
            const fieldMapping = {
              'IssuerAccountId': 'issuer_account_id',
              'Symbol': 'symbol',
              'Isin': 'isin',
              'Name': 'name',
              'TotalUnitsIssue': 'total_units_issue',
              'IssuePrice': 'issue_price',
              'LastPrice': 'last_price',
              'IssueDate': 'issue_date',
              'DateJouissance': 'date_jouissance'
            };
            
            const mappedField = fieldMapping[field] || field.toLowerCase();
            mappedErrors[mappedField] = Array.isArray(messages) ? messages[0] : messages;
          });
          setErrors(mappedErrors);
        }
        
        toast.error(result.message || "Erreur lors de l'opération");
      }
    } catch (error) {
      
      // Gérer les erreurs d'API structurées
      if (error.errors && Object.keys(error.errors).length > 0) {
        const mappedErrors = {};
        Object.entries(error.errors).forEach(([field, messages]) => {
          const fieldMapping = {
            'IssuerAccountId': 'issuer_account_id',
            'Symbol': 'symbol',
            'Isin': 'isin',
            'Name': 'name',
            'TotalUnitsIssue': 'total_units_issue',
            'IssuePrice': 'issue_price',
            'LastPrice': 'last_price',
            'IssueDate': 'issue_date',
            'DateJouissance': 'date_jouissance'
          };
          
          const mappedField = fieldMapping[field] || field.toLowerCase();
          mappedErrors[mappedField] = Array.isArray(messages) ? messages[0] : messages;
        });
        setErrors(mappedErrors);
      } else {
        setErrors({ 
          _form: error.message || "Une erreur est survenue lors de la soumission" 
        });
      }
      
      toast.error(error.message || "Erreur lors de l'opération");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Fermer le modal
   */
  const handleClose = () => {
    // Réinitialiser le formulaire
    if (!isEditMode) {
      setFormData({
        issuer_account_id: '',
        symbol: '',
        isin: '',
        name: '',
        target_market: '',
        type: '',
        repayable: '',
        total_units_issue: '',
        maturity: '',
        coupon_frequency: '',
        coupon_rate: '',
        issue_price: '',
        last_price: '',
        issue_date: '',
        date_jouissance: ''
      });
      setErrors({});
      setTouched({});
    }
    
    // Fermer le modal Bootstrap
    const modal = document.getElementById(modalId);
    if (modal) {
      const bootstrapModal = bootstrap.Modal.getInstance(modal);
      bootstrapModal?.hide();
    }
    
    // Appeler le callback de fermeture
    if (onClose) onClose();
  };

  // Déterminer le type de titre pour l'affichage conditionnel
  const isFixedIncome = formData.type === "Bonds" || formData.type === "Notes";
  const isEquity = formData.type === "Shares" || formData.type === "ETF";

  // Classe CSS pour les champs
  const getFieldClass = (fieldName) => {
    return `form-control form-control-lg ${errors[fieldName] ? "is-invalid" : touched[fieldName] ? "is-valid" : ""}`;
  };

  return (
    <>
      <div className="modal fade" id={modalId} tabIndex="-1" aria-labelledby="securityModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            {/* En-tête du modal */}
            <div className="modal-header bg-white text-white">
              <h1 className="modal-title fs-5" id="securityModalLabel">
                <i className={`fas ${isEditMode ? 'fa-edit' : 'fa-plus-circle'} me-2`}></i>
                {isEditMode ? 'Modifier un Titre' : 'Créer un Nouveau Titre'}
              </h1>
              <button 
                type="button" 
                className="btn-close btn-close-blue" 
                data-bs-dismiss="modal" 
                aria-label="Close"
                onClick={handleClose}
              ></button>
            </div>
            
            {/* Corps du modal */}
            <div className="modal-body">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-3 text-muted">Chargement des données du titre...</p>
                </div>
              ) : (
                <>
                  {noResults && (
                    <div className="alert alert-warning">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Aucun compte trouvé pour ce domaine. Veuillez d'abord créer un compte.
                    </div>
                  )}
                  
                  {errors._form && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-circle me-2"></i>
                      {errors._form}
                    </div>
                  )}
                  
                  <div className="card border-0 shadow-sm">
                    <form className="profile-form" onSubmit={handleSubmit}>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          {/* Colonne 1 - Informations de base */}
                          <div className="col-lg-6 bg-light p-3 rounded mb-3">
                            <h5 className="text-primary mb-4 border-bottom pb-2">
                              <i className="fas fa-info-circle me-2"></i>
                              Informations de Base
                            </h5>
                            
                            {/* Type */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Type de Titre <span className="text-danger">*</span></label>
                              <select
                                className={getFieldClass('type')}
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                onBlur={() => handleBlur('type')}
                                disabled={isEditMode} // Le type ne peut pas être modifié en édition
                              >
                                <option value="">-- Sélectionner le type --</option>
                                <option value="Shares">Action</option>
                                <option value="Bonds">Obligation</option>
                                <option value="Notes">Note</option>
                                <option value="ETF">ETF</option>
                                <option value="Other">Autre</option>
                              </select>
                              {errors.type && <div className="invalid-feedback d-block">{errors.type}</div>}
                              {isEditMode && (
                                <div className="form-text text-muted">
                                  <i className="fas fa-info-circle me-1"></i>
                                  Le type de titre ne peut pas être modifié après création
                                </div>
                              )}
                            </div>

                            {/* Issuer Account avec SearchableSelect */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Compte Porteur <span className="text-danger">*</span></label>
                              <SearchableSelect
                                options={accounts.map(account => ({
                                  value: account.id,
                                  label: ["Issuer"].includes(account.accountTypeName)
                                    ? account.companyName
                                    : `${account.firstName} ${account.lastName}`,
                                  ...account
                                }))}
                                value={formData.issuer_account_id}
                                onChange={(selected) => {
                                  handleChange({
                                    target: {
                                      name: 'issuer_account_id',
                                      value: selected ? selected.value.toString() : ''
                                    }
                                  });
                                  handleBlur('issuer_account_id');
                                }}
                                label="Sélectionnez un Porteur"
                                error={errors.issuer_account_id}
                                required={true}
                                helpText="Sélectionnez le compte qui émet le titre"
                                searchKeys={['firstName', 'lastName', 'companyName', 'email']}
                                isDisabled={isEditMode} // Le compte ne peut pas être modifié en édition
                              />
                              {isEditMode && (
                                <div className="form-text text-muted">
                                  <i className="fas fa-info-circle me-1"></i>
                                  Le compte porteur ne peut pas être modifié après création
                                </div>
                              )}
                            </div>

                            {/* Symbol */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Symbole <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className={getFieldClass('symbol')}
                                name="symbol"
                                value={formData.symbol}
                                onChange={handleChange}
                                onBlur={() => handleBlur('symbol')}
                                placeholder="Ex: AAPL"
                                maxLength={10}
                                disabled={isEditMode} // Le symbole ne peut pas être modifié en édition
                              />
                              {errors.symbol && <div className="invalid-feedback d-block">{errors.symbol}</div>}
                              <div className="form-text">
                                Maximum 10 caractères - Ne peut pas être modifié après création
                              </div>
                            </div>

                            {/* ISIN */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">ISIN <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className={getFieldClass('isin')}
                                name="isin"
                                value={formData.isin}
                                onChange={handleChange}
                                onBlur={() => handleBlur('isin')}
                                placeholder="Ex: US0378331005"
                                maxLength={12}
                                disabled={isEditMode} // L'ISIN ne peut pas être modifié en édition
                              />
                              {errors.isin && <div className="invalid-feedback d-block">{errors.isin}</div>}
                              <div className="form-text">
                                12 caractères exactement - Format: 2 lettres + 10 caractères
                              </div>
                            </div>

                            {/* Name */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Nom du Titre <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                className={getFieldClass('name')}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                onBlur={() => handleBlur('name')}
                                placeholder="Ex: Apple Inc."
                                maxLength={100}
                              />
                              {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                            </div>

                            {/* Target Market */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Marché Cible</label>
                              <select
                                className="form-select form-control-lg"
                                name="target_market"
                                value={formData.target_market}
                                onChange={handleChange}
                              >
                                <option value="">-- Sélectionner le marché cible --</option>
                                <option value="Public">Coté</option>
                                <option value="Private">Non Coté</option>
                                <option value="Monetary">Monétaire</option>
                              </select>
                              <div className="form-text">
                                Définit la visibilité et les règles du titre
                              </div>
                            </div>
                          </div>

                          {/* Colonne 2 - Détails spécifiques */}
                          <div className="col-lg-6 bg-light p-3 rounded mb-3">
                            <h5 className="text-primary mb-4 border-bottom pb-2">
                              <i className="fas fa-cog me-2"></i>
                              Détails du Titre
                            </h5>

                            {/* Total Units Issue */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Nombre Total d'Unités <span className="text-danger">*</span></label>
                              <input
                                type="number"
                                className={getFieldClass('total_units_issue')}
                                name="total_units_issue"
                                value={formData.total_units_issue}
                                onChange={handleChange}
                                onBlur={() => handleBlur('total_units_issue')}
                                placeholder="Ex: 1000000"
                                min="1"
                                step="1"
                              />
                              {errors.total_units_issue && <div className="invalid-feedback d-block">{errors.total_units_issue}</div>}
                              <div className="form-text">
                                Le nombre total de parts/actions émises
                              </div>
                            </div>

                            {/* Issue Price */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Prix d'Émission <span className="text-danger">*</span></label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className={getFieldClass('issue_price')}
                                  name="issue_price"
                                  value={formData.issue_price}
                                  onChange={handleChange}
                                  onBlur={() => handleBlur('issue_price')}
                                  placeholder="Ex: 100.00"
                                  step="0.01"
                                  min="0.01"
                                />
                                <span className="input-group-text">FCFA</span>
                              </div>
                              {errors.issue_price && <div className="invalid-feedback d-block">{errors.issue_price}</div>}
                            </div>

                            {/* Last Price */}
                            <div className="mb-3">
                              <label className="form-label fw-semibold">Dernier Prix</label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className={getFieldClass('last_price')}
                                  name="last_price"
                                  value={formData.last_price}
                                  onChange={handleChange}
                                  onBlur={() => handleBlur('last_price')}
                                  placeholder="Par défaut, même que le prix d'émission"
                                  step="0.01"
                                  min="0.01"
                                />
                                <span className="input-group-text">FCFA</span>
                              </div>
                              {errors.last_price && <div className="invalid-feedback d-block">{errors.last_price}</div>}
                              <div className="form-text">
                                Si vide, sera automatiquement défini au prix d'émission
                              </div>
                            </div>

                            {/* Target Market déjà dans la première colonne */}

                            {/* Section conditionnelle pour les titres à revenu fixe */}
                            {isFixedIncome && (
                              <div className="bg-light p-3 rounded mb-3">
                                <h6 className="text-primary mb-3">
                                  <i className="fas fa-chart-line me-2"></i>
                                  Détails des Coupons
                                </h6>

                                {/* Coupon Frequency */}
                                <div className="mb-3">
                                  <label className="form-label fw-semibold">Fréquence du Coupon <span className="text-danger">*</span></label>
                                  <select
                                    className={getFieldClass('coupon_frequency')}
                                    name="coupon_frequency"
                                    value={formData.coupon_frequency}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('coupon_frequency')}
                                  >
                                    <option value="">-- Sélectionner la fréquence --</option>
                                    <option value="Monthly">Mensuelle</option>
                                    <option value="Quarterly">Trimestrielle</option>
                                    <option value="Semiannual">Semestrielle</option>
                                    <option value="Annually">Annuelle</option>
                                    <option value="At Maturity">À l'échéance</option>
                                  </select>
                                  {errors.coupon_frequency && <div className="invalid-feedback d-block">{errors.coupon_frequency}</div>}
                                </div>

                                {/* Coupon Rate */}
                                <div className="mb-3">
                                  <label className="form-label fw-semibold">Taux du Coupon (%)</label>
                                  <div className="input-group">
                                    <input
                                      type="number"
                                      className={getFieldClass('coupon_rate')}
                                      name="coupon_rate"
                                      value={formData.coupon_rate}
                                      onChange={handleChange}
                                      onBlur={() => handleBlur('coupon_rate')}
                                      placeholder="Ex: 2.5"
                                      step="any"
                                      min="0"
                                      max="100"
                                    />
                                    <span className="input-group-text">%</span>
                                  </div>
                                  {errors.coupon_rate && <div className="invalid-feedback d-block">{errors.coupon_rate}</div>}
                                  <div className="form-text">
                                    Taux d'intérêt annuel du coupon (optionnel)
                                  </div>
                                </div>

                                {/* Repayable */}
                                <div className="mb-3">
                                  <label className="form-label fw-semibold">Remboursable</label>
                                  <select
                                    className="form-select form-control-lg"
                                    name="repayable"
                                    value={formData.repayable}
                                    onChange={handleChange}
                                  >
                                    <option value="">-- Sélectionner --</option>
                                    <option value="true">Oui</option>
                                    <option value="false">Non</option>
                                  </select>
                                  <div className="form-text">
                                    Indique si le titre est remboursable avant échéance
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Section pour les dates */}
                            <div className="bg-light p-3 rounded">
                              <h6 className="text-primary mb-3">
                                <i className="fas fa-calendar-alt me-2"></i>
                                Dates Importantes
                              </h6>

                              {/* Issue Date */}
                              <div className="mb-3">
                                <label className="form-label fw-semibold">
                                  {isFixedIncome ? "Date d'Émission *" : "Date d'Émission"}
                                </label>
                                <input
                                  type="date"
                                  className={getFieldClass('issue_date')}
                                  name="issue_date"
                                  value={formData.issue_date}
                                  onChange={handleChange}
                                  onBlur={() => handleBlur('issue_date')}
                                  max={new Date().toISOString().split('T')[0]}
                                />
                                {errors.issue_date && <div className="invalid-feedback d-block">{errors.issue_date}</div>}
                                {isEquity && !formData.issue_date && (
                                  <div className="form-text text-muted">
                                    <i className="fas fa-info-circle me-1"></i>
                                    Si non renseignée, la date actuelle sera utilisée
                                  </div>
                                )}
                              </div>

                              {/* Maturity Date - Seulement pour revenu fixe */}
                              {isFixedIncome && (
                                <div className="mb-3">
                                  <label className="form-label fw-semibold">Date d'Échéance <span className="text-danger">*</span></label>
                                  <input
                                    type="date"
                                    className={getFieldClass('maturity')}
                                    name="maturity"
                                    value={formData.maturity}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('maturity')}
                                    min={formData.issue_date || new Date().toISOString().split('T')[0]}
                                  />
                                  {errors.maturity && <div className="invalid-feedback d-block">{errors.maturity}</div>}
                                  <div className="form-text">
                                    Doit être postérieure à la date d'émission
                                  </div>
                                </div>
                              )}

                              {/* Date de Jouissance */}
                              <div className="mb-3">
                                <label className="form-label fw-semibold">Date de Jouissance</label>
                                <input
                                  type="date"
                                  className={getFieldClass('date_jouissance')}
                                  name="date_jouissance"
                                  value={formData.date_jouissance}
                                  onChange={handleChange}
                                  onBlur={() => handleBlur('date_jouissance')}
                                  max={formData.issue_date || new Date().toISOString().split('T')[0]}
                                />
                                {errors.date_jouissance && <div className="invalid-feedback d-block">{errors.date_jouissance}</div>}
                                <div className="form-text">
                                  Date à partir de laquelle les intérêts commencent à courir
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Pied du modal */}
                      <div className="card-footer bg-light p-4 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted small">
                            <i className="fas fa-info-circle me-1"></i>
                            Les champs marqués d'un <span className="text-danger fw-bold">*</span> sont obligatoires
                            {isEditMode && (
                              <div className="mt-1">
                                <i className="fas fa-exclamation-triangle me-1 text-warning"></i>
                                Certains champs ne peuvent pas être modifiés après création
                              </div>
                            )}
                          </div>
                          
                          <div className="d-flex gap-2">
                            {/* Bouton Annuler */}
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary px-4"
                              onClick={handleClose}
                              disabled={isSubmitting}
                            >
                              <i className="fas fa-times me-2"></i>
                              Annuler
                            </button>
                            
                            {/* Bouton Soumettre */}
                            <button 
                              type="submit" 
                              className="btn btn-primary px-4"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  {isEditMode ? 'Mise à jour...' : 'Création...'}
                                </>
                              ) : (
                                <>
                                  <i className={`fas ${isEditMode ? 'fa-save' : 'fa-plus'} me-2`}></i>
                                  {isEditMode ? 'Mettre à jour' : 'Créer le Titre'}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SecurityForm;